import { Box, Card, Stack, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { dashboardCardSx } from "../layout/dashboardStyles";

interface DonutCardData {
  value: number;
  label: string;
  color: string;
}

interface DonutCardProps {
  title: string;
  data: DonutCardData[];
  totalLabel?: string;
  centerLabel?: string;
}

export const DonutCard: React.FC<DonutCardProps> = ({
  title,
  data,
  totalLabel,
  centerLabel,
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const main = data[0];
  const mainPercent = total ? Math.round((main.value / total) * 100) : 0;
  return (
    <Card
      sx={{
        ...dashboardCardSx,
        width: "100%",
        minWidth: 0,
        p: { xs: 1.5, sm: 2 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ position: "relative", mb: 2, mt: 1 }}>
        <Box
          sx={{
            p: 1.5,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <PieChart
            series={[
              {
                data: data.map((item, idx) => ({
                  id: idx,
                  value: item.value,
                  label: item.label,
                  color: item.color,
                })),
                innerRadius: 46,
                outerRadius: 60,
                cx: 60,
                cy: 60,
              },
            ]}
            width={130}
            height={130}
            sx={{
              ".MuiChartsLegend-root": { display: "none" },
              ".MuiPieArc-root": { stroke: "#181C23", strokeWidth: 2 },
            }}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -48%)",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: main.color }}>
            {mainPercent}%
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize={12}>
            {centerLabel || main.label}
          </Typography>
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        width="100%"
        mt={1}
        flexWrap="wrap"
        useFlexGap
      >
        {data.map((item, idx) => {
          const percent = total ? Math.round((item.value / total) * 100) : 0;
          return (
            <Box key={item.label} sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  background: item.color,
                  display: "inline-block",
                  mr: 0.7,
                  verticalAlign: "middle",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: item.color,
                  fontWeight: 600,
                  fontSize: "0.98em",
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {item.value.toLocaleString("es-ES")} ({percent}%)
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {totalLabel && (
        <Typography
          variant="caption"
          color="text.secondary"
          fontSize={12}
          sx={{ mt: 2 }}
        >
          {totalLabel}: <b>{total.toLocaleString("es-ES")}</b>
        </Typography>
      )}
    </Card>
  );
};
